import {
  CalendarDays,
  Car,
  ClipboardCheck,
  Receipt,
  UserCheck,
  Users,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatBRL, formatDate } from '@/lib/format';
import { useBillingPreview, useTenantCurrent, useTenantUsage } from './api';

interface UsageBarProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  current: number;
  max?: number | null;
  unit?: string;
  description?: string;
}

function UsageBar({ icon: Icon, label, current, max, unit = '', description }: UsageBarProps) {
  const hasLimit = max != null && max > 0;
  const percent = hasLimit ? Math.min(100, Math.round((current / (max as number)) * 100)) : 0;
  const tone =
    percent >= 90 ? 'destructive' : percent >= 75 ? 'warning' : 'primary';
  const barClass =
    tone === 'destructive'
      ? 'bg-destructive'
      : tone === 'warning'
        ? 'bg-warning'
        : 'bg-primary';

  return (
    <div className="space-y-2 rounded-lg border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex size-8 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Icon className="size-4" />
          </div>
          <div>
            <p className="text-sm font-medium">{label}</p>
            {description && (
              <p className="text-xs text-muted-foreground">{description}</p>
            )}
          </div>
        </div>
        <div className="text-right">
          <p className="font-mono text-xl font-semibold tabular-nums">
            {current.toLocaleString('pt-BR')}
            {hasLimit && (
              <span className="text-sm font-normal text-muted-foreground">
                {' / '}
                {(max as number).toLocaleString('pt-BR')}
              </span>
            )}
            {unit && <span className="ml-1 text-xs text-muted-foreground">{unit}</span>}
          </p>
          {hasLimit && (
            <p className="text-[11px] text-muted-foreground">{percent}% do limite</p>
          )}
          {!hasLimit && (
            <p className="text-[11px] text-muted-foreground">sem limite no plano</p>
          )}
        </div>
      </div>
      {hasLimit && (
        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={`h-full ${barClass} transition-all`}
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
    </div>
  );
}

const STATUS_LABEL: Record<string, { label: string; variant: 'success' | 'warning' | 'destructive' | 'outline' }> = {
  trial: { label: 'Ativo', variant: 'success' },
  active: { label: 'Ativo', variant: 'success' },
  past_due: { label: 'Pagamento pendente', variant: 'destructive' },
  suspended: { label: 'Suspenso', variant: 'destructive' },
  cancelled: { label: 'Cancelado', variant: 'outline' },
};

export function PlanSection() {
  const tenantQuery = useTenantCurrent();
  const usageQuery = useTenantUsage();

  const t = tenantQuery.data;
  const u = usageQuery.data;
  const statusInfo = t ? STATUS_LABEL[t.status] ?? { label: t.status, variant: 'outline' as const } : null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Uso atual</CardTitle>
          <CardDescription>
            Acompanhe o consumo de cada recurso na sua operação.
            {t && statusInfo && (
              <span className="ml-2 inline-flex items-center gap-1">
                ·{' '}
                <Badge variant={statusInfo.variant} className="text-[10px]">
                  Conta {statusInfo.label.toLowerCase()}
                </Badge>
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {usageQuery.isLoading || !u || !t ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <UsageBar
                icon={Car}
                label="Veículos"
                current={u.vehicles}
                description={`${u.activeVehicles} ativos · cobrança por veículo`}
              />
              <UsageBar
                icon={Users}
                label="Motoristas"
                current={u.drivers}
                description="cadastrados na operação"
              />
              <UsageBar
                icon={UserCheck}
                label="Usuários do painel"
                current={u.users}
                description="com acesso ao sistema"
              />
              <UsageBar
                icon={ClipboardCheck}
                label="Checklists concluídos"
                current={u.checklistsTotal}
                description={`${u.checklistsToday} executados hoje`}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <BillingCard />
    </div>
  );
}

function BillingCard() {
  const billingQuery = useBillingPreview();
  const b = billingQuery.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Receipt className="size-4" />
          Cobrança do mês
        </CardTitle>
        <CardDescription>
          Você paga apenas pelos veículos ativos cadastrados. Quanto mais frota, mais paga.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {billingQuery.isLoading || !b ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <div className="space-y-4">
            {/* Cálculo principal */}
            <div className="rounded-lg border border-border bg-gradient-to-br from-primary/5 to-transparent p-5">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                Valor estimado
              </p>
              <div className="mt-2 flex flex-wrap items-baseline gap-2">
                <span className="font-mono text-3xl font-semibold tabular-nums">
                  {formatBRL(b.subtotalCents)}
                </span>
                <span className="text-sm text-muted-foreground">/mês</span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                <span className="font-mono font-medium">{b.activeVehicleCount}</span>
                {' '}veícul{b.activeVehicleCount === 1 ? 'o' : 'os'} ativ{b.activeVehicleCount === 1 ? 'o' : 'os'} ×{' '}
                <span className="font-mono font-medium">{formatBRL(b.pricePerVehicleCents)}</span>
                {' '}por veículo
              </p>
            </div>

            {/* Próxima fatura */}
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-border p-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                  <CalendarDays className="size-3.5" />
                  Próxima fatura
                </div>
                <p className="mt-2 text-sm font-medium">{formatDate(b.nextInvoiceAt)}</p>
                <p className="text-xs text-muted-foreground">
                  em {b.daysUntilInvoice} dia{b.daysUntilInvoice === 1 ? '' : 's'}
                </p>
              </div>
              <div className="rounded-lg border border-border p-4">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  A pagar
                </p>
                <p className="mt-2 font-mono text-lg font-semibold tabular-nums">
                  {formatBRL(b.totalDueCents)}
                </p>
                <p className="text-xs text-muted-foreground">na data de fechamento</p>
              </div>
              <div className="rounded-lg border border-border p-4">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  Forma de pagamento
                </p>
                <p className="mt-2 text-sm font-medium text-muted-foreground">
                  Não configurado
                </p>
                <p className="text-xs text-muted-foreground">
                  será habilitado em breve
                </p>
              </div>
            </div>

            {b.vehicleCount > b.activeVehicleCount && (
              <p className="text-xs text-muted-foreground">
                Você tem {b.vehicleCount - b.activeVehicleCount}{' '}
                veícul{b.vehicleCount - b.activeVehicleCount === 1 ? 'o inativo que não é cobrado' : 'os inativos que não são cobrados'}.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

